import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle2, Calendar, Heart, ArrowRight, Loader2 } from 'lucide-react';
import apiClient from '../services/api';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 12;
    const retryDelay = 2500;
    let isMounted = true;

    const fetchSubscriptionStatus = async () => {
      if (!sessionId) {
        setError('No subscription session found');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/subscriptions/status/${sessionId}`);
        
        if (!isMounted) return;
        
        // Check if subscription is confirmed (active, trialing, stripe_verified, or complete)
        const isSuccess = response.data.status === 'active' || 
                         response.data.status === 'trialing' || 
                         response.data.status === 'complete' ||
                         response.data.stripe_verified === true;
        
        if (isSuccess) {
          setSubscription(response.data);
          setLoading(false);
        } else if (response.data.status === 'pending' && retryCount < maxRetries) {
          // Still pending - webhook might not have processed yet, retry
          retryCount++;
          console.log(`Subscription pending, retrying (${retryCount}/${maxRetries})...`);
          setTimeout(fetchSubscriptionStatus, retryDelay);
        } else {
          // Max retries or non-pending non-active status - still show as success if we got data
          setSubscription(response.data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        
        if (!isMounted) return;
        
        // Retry on 404 - subscription record might not exist yet
        if (err.response?.status === 404 && retryCount < maxRetries) {
          retryCount++;
          console.log(`Subscription not found, retrying (${retryCount}/${maxRetries})...`);
          setTimeout(fetchSubscriptionStatus, retryDelay);
          return;
        }
        
        // After max retries, show a more helpful message but don't block
        // If user received email confirmation, they're good
        setSubscription({
          status: 'processing',
          amount: 0,
          campaign_title: 'Your Campaign',
          message: 'Your subscription is being processed. If you received a confirmation email, your subscription is active.'
        });
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
    
    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-violet-500 mx-auto mb-4" />
            <p className="text-muted-foreground">Confirming your subscription...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    // Even if there's an error, show a success-like state since the user completed payment
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="py-12">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500 to-indigo-600 p-8 text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Thank You for Your Support!
                </h1>
                <p className="text-white/80">
                  Your subscription is being processed
                </p>
              </div>
              <div className="p-8 text-center">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
                  <p className="text-emerald-400 font-medium">
                    If you received a confirmation email from Stripe, your subscription is active!
                  </p>
                </div>
                <p className="text-muted-foreground text-sm mb-6">
                  It may take a few moments for our system to confirm your subscription. 
                  You can safely navigate away - your monthly donation will be processed automatically.
                </p>
                <Link to="/discover">
                  <Button className="bg-gradient-to-r from-violet-500 to-indigo-600">
                    Browse Campaigns
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!subscription && !loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="p-8 bg-card rounded-2xl border border-border">
            <h1 className="text-2xl font-bold text-foreground mb-4">
              {error || 'Subscription not found'}
            </h1>
            <p className="text-muted-foreground mb-6">
              Please check your email for confirmation or contact support.
            </p>
            <Link to="/discover">
              <Button>Browse Campaigns</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show processing state as success (user got email confirmation)
  if (subscription?.status === 'processing' || subscription?.message) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="py-12">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500 to-indigo-600 p-8 text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Subscription Started!
                </h1>
                <p className="text-white/80">
                  Your monthly donation is being processed
                </p>
              </div>
              <div className="p-8 text-center">
                <p className="text-muted-foreground mb-6">
                  {subscription.message || 'If you received a confirmation email, your subscription is active.'}
                </p>
                <Link to="/discover">
                  <Button>Browse Campaigns</Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          {/* Success Card */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-500 to-indigo-600 p-8 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Monthly Donation Started!
              </h1>
              <p className="text-white/80">
                Thank you for your ongoing support
              </p>
            </div>
            
            {/* Details */}
            <div className="p-8">
              {/* Amount */}
              <div className="text-center mb-8">
                <p className="text-sm text-muted-foreground mb-1">Monthly Amount</p>
                <p className="text-5xl font-bold bg-gradient-to-r from-violet-500 to-indigo-600 bg-clip-text text-transparent">
                  ${subscription.amount?.toFixed(2)}/mo
                </p>
              </div>
              
              {/* Campaign Info */}
              <div className="bg-secondary/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-1">Supporting</p>
                <p className="font-semibold text-foreground">
                  {subscription.campaign_title || 'Campaign'}
                </p>
              </div>
              
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-violet-500/10 rounded-xl p-4 text-center">
                  <Calendar className="w-6 h-6 text-violet-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Billed Monthly</p>
                  <p className="font-semibold text-foreground">Every 30 days</p>
                </div>
                <div className="bg-violet-500/10 rounded-xl p-4 text-center">
                  <Heart className="w-6 h-6 text-violet-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-semibold text-foreground capitalize">
                    {subscription.status === 'pending' ? 'Processing' : subscription.status}
                  </p>
                </div>
              </div>
              
              {/* What's Next */}
              <div className="border border-border rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-foreground mb-3">What happens next?</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>You'll receive a confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Your card will be charged ${subscription.amount?.toFixed(2)} each month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>You can cancel anytime from your dashboard</span>
                  </li>
                </ul>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/campaign/${subscription.campaign_id}`} className="flex-1">
                  <Button variant="outline" className="w-full">
                    View Campaign
                  </Button>
                </Link>
                <Link to="/dashboard?mode=donor&tab=recurring" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700">
                    Manage Subscription
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Share Section */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">
              Spread the word and help this campaign reach its goal!
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const text = `I just set up a monthly donation to support this campaign! Join me in making a difference.`;
                  const url = `${window.location.origin}/campaign/${subscription.campaign_id}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                }}
              >
                Share on Twitter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const url = `${window.location.origin}/campaign/${subscription.campaign_id}`;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
              >
                Share on Facebook
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SubscriptionSuccessPage;
